import { APP_NAME } from '../utils/branding';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCamera, FaCloudUploadAlt, FaCheckCircle, FaTimes } from 'react-icons/fa';
import { uploadProfilePhoto } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { compressImageForUpload } from '../utils/imageCompression';

function ProfileUpload() {
  const { user, setUser } = useAuth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    return () => stopCamera();
  }, []);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 16 * 1024 * 1024) {
        setError('Le fichier doit faire moins de 16 Mo');
        return;
      }

      if (!selectedFile.type.startsWith('image/')) {
        setError('Veuillez sélectionner un fichier image');
        return;
      }

      setFile(selectedFile);
      setPreview(URL.createObjectURL(selectedFile));
      setError('');
      stopCamera();
    }
  };

  const startCamera = async () => {
    setError('');
    setSuccess('');
    setCameraLoading(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 960 },
          height: { ideal: 720 },
        },
        audio: false,
      });

      streamRef.current = stream;
      setCameraActive(true);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      setError("Impossible d'accéder à votre caméra. Autorisez l'accès ou importez une photo à la place.");
    } finally {
      setCameraLoading(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
  };

  const captureFromCamera = () => {
    if (!videoRef.current) {
      setError("La caméra n'est pas encore prête.");
      return;
    }

    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 960;
    canvas.height = video.videoHeight || 720;
    const context = canvas.getContext('2d');
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (!blob) {
        setError("Impossible de capturer une photo depuis la caméra.");
        return;
      }

      const capturedFile = new File([blob], `webcam-profile-${Date.now()}.jpg`, {
        type: 'image/jpeg',
      });
      setFile(capturedFile);
      setPreview(URL.createObjectURL(blob));
      setError('');
      stopCamera();
    }, 'image/jpeg', 0.92);
  };

  const handleUpload = async (photoFile = file) => {
    if (!photoFile) {
      setError("Veuillez d'abord sélectionner une photo");
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(photoFile.type)) {
      setError('Veuillez importer une image JPG, PNG ou WEBP.');
      return;
    }

    if (photoFile.size > 16 * 1024 * 1024) {
      setError('La photo de profil doit faire 16 Mo ou moins avant compression.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const uploadFile = await compressImageForUpload(photoFile);
      const photoFormData = new FormData();
      photoFormData.append('photo', uploadFile);
      const photoResponse = await uploadProfilePhoto(photoFormData);

      const updatedUser = { ...user, profile_photo: photoResponse.profile_photo };

      setSuccess('Photo de profil enregistrée. Vous pouvez rechercher vos photos dans une galerie.');

      if (setUser) setUser(updatedUser);
      localStorage.setItem('userData', JSON.stringify(updatedUser));

      setTimeout(() => {
        navigate('/my-photos');
      }, 2000);
    } catch (err) {
      setError(err.message || "L'import a échoué. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="upload-container">
        <h2 style={{ textAlign: 'center', color: 'var(--primary-orange)', marginBottom: '1rem' }}>
          Scanner votre visage
        </h2>
        <p style={{ textAlign: 'center', color: 'var(--gray)', marginBottom: '2rem' }}>
          Utilisez votre webcam ou importez une photo nette de votre visage pour permettre à {APP_NAME}
          de rechercher vos correspondances dans les galeries d'événements.
        </p>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          <button
            type="button"
            onClick={cameraActive ? stopCamera : startCamera}
            className="btn-submit"
            disabled={loading || cameraLoading}
            style={{
              background: cameraActive ? '#EF4444' : 'var(--surface-2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
          >
            {cameraActive ? <FaTimes /> : <FaCamera />}
            {cameraLoading ? 'Ouverture de la caméra...' : cameraActive ? 'Fermer la caméra' : 'Utiliser la webcam'}
          </button>

          {cameraActive && (
            <button
              type="button"
              onClick={captureFromCamera}
              className="btn-submit"
              disabled={loading}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              <FaCamera /> Capturer le visage
            </button>
          )}
        </div>

        {cameraActive && (
          <div style={{
            marginBottom: '1.5rem',
            background: 'var(--bg)',
            borderRadius: '12px',
            overflow: 'hidden',
            border: '2px solid var(--light-green)'
          }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: '100%',
                maxHeight: '420px',
                objectFit: 'cover',
                display: 'block'
              }}
            />
          </div>
        )}

        <div className="upload-zone" onClick={() => document.getElementById('fileInput').click()}>
          {preview ? (
            <img src={preview} alt="Aperçu" className="preview-image" />
          ) : (
            <>
              <div className="upload-icon">
                <FaCloudUploadAlt />
              </div>
              <p>Cliquez pour sélectionner une photo à la place</p>
              <p style={{ fontSize: '0.9rem', color: 'var(--gray)', marginTop: '0.5rem' }}>
                Formats acceptés : JPG, PNG (max. 16 Mo)
              </p>
            </>
          )}
        </div>

        <input
          id="fileInput"
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />

        {file && (
          <div style={{ marginTop: '1rem' }}>
            <p style={{ color: 'var(--gray)', marginBottom: '1rem' }}>
              Sélectionné : {file.name} ({(file.size / 1024 / 1024).toFixed(2)} Mo)
            </p>
            <button
              onClick={handleUpload}
              className="btn-submit"
              disabled={loading}
            >
              {loading ? 'Enregistrement...' : 'Enregistrer le scan et trouver mes photos'}
            </button>
          </div>
        )}

        <div style={{
          marginTop: '2rem',
          padding: '1.5rem',
          background: 'var(--bg)',
          borderRadius: '12px',
          border: '2px solid var(--light-green)'
        }}>
          <h4 style={{ color: 'var(--primary-green)', marginBottom: '1rem' }}>
            <FaCheckCircle /> Conseils pour de meilleurs résultats
          </h4>
          <ul style={{ color: 'var(--gray)', lineHeight: '1.8', paddingLeft: '1.5rem' }}>
            <li>Utilisez une photo récente et nette de votre visage</li>
            <li>Privilégiez un bon éclairage et peu d'ombres</li>
            <li>Votre visage doit être bien visible et centré</li>
            <li>Évitez les lunettes de soleil ou les couvre-visages</li>
            <li>Les photos haute résolution donnent les meilleurs résultats</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default ProfileUpload;
